import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '@store/index'
import {
  Accordion,
  Dropdown,
  Form,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import { Badge, EmptyState, type BadgeMode } from '@enamelai/design-system'
import SearchWithFilter, {
  FilterSelection,
} from '@components/common/SearchWithFilter/SearchWithFilter.component'
import { DateRangePickerComponent } from '@syncfusion/ej2-react-calendars'
import {
  HistoryMonthAccordion,
  HistoryPreviousSkeleton,
  HistorySectionHeader,
  HistoryUpcomingSkeleton,
  groupAppointmentsByMonth,
  getHistoryStatusColor,
  getHistoryStatusLabel,
} from '@components/layout/SidePanel/History.component'
import StatusBadge from '@components/common/StatusBadge/StatusBadge.component'
import { AppointmentStatusMap } from '@utils/appointment.util'
import { useTheme } from '@context/ThemeContext'
import {
  deleteAppointmentById,
  getAppointmentsByProviderId,
  updateAppointmentById,
} from '@services/api/appointment.api'
import moment from 'moment'
import {
  AppointmentRequest,
  AppointmentResponse,
  AppointmentStatus,
} from '@dataTypes/appointment.type'
import ConfirmationModal from '@components/common/ConfirmationModal/ConfirmationModal.component'
import {
  deleteModalBody,
  deleteModalFooter,
} from '@components/pages/Appointments/DayCalendar/DayCalendarTemplate'
import { useAppSelector } from '@store/hooks'
import { refreshFromPatient } from '@store/reducers/patientmanagement.reducer'
import { Provider } from '@dataTypes/provider.types'
import { ReactComponent as ArrowRightMonthIcon } from '@assets/images/Sidepanel/arrow-right-month.svg'
import { ReactComponent as ArrowDownMonthIcon } from '@assets/images/Sidepanel/arrow-down-month.svg'
import { htmlToPlainTextPreview } from '@utils/htmlPlainText.util'
import {
  formatAppointmentLocalDate,
  formatAppointmentLocalStartTime,
  getAppointmentLocalMoment,
} from '@utils/time.util'
// Mock appointments for development when backend is unavailable (production-safe)
import { MOCK_PREVIOUS_APPOINTMENTS } from '@assets/mockdata/Provider/mockAppointments.data'

// Helper to check if an appointment is mock data
const isMockAppointment = (appointmentId: string): boolean => {
  return appointmentId?.startsWith('mock-appt-') ?? false
}

export const editDeleteDDComponent = (
  id?: string,
  onEdit?: (id: string) => void,
  onDelete?: (id: string) => void,
): JSX.Element => {
  return (
    <Dropdown className="" style={{ marginBottom: '2px' }}>
      <Dropdown.Toggle
        variant="light"
        id="dropdown-history-sidepanel"
        className="edit-delete-dd"
      >
        <i className="ti ti-dots-vertical"></i>
      </Dropdown.Toggle>
      <Dropdown.Menu className="edit-delete-menu">
        <Dropdown.Item onClick={onEdit && (() => onEdit(id || ''))}>
          <i className="ti ti-pencil me-1"></i>Edit
        </Dropdown.Item>
        <Dropdown.Item onClick={onDelete && (() => onDelete(id || ''))}>
          <i className="ti ti-trash me-1"></i>Delete
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  )
}

type HistoryProps = {
  provider: Provider | null
}

const History: React.FC<HistoryProps> = ({ provider }): React.ReactElement => {
  const dispatch: AppDispatch = useDispatch()
  const { resolvedTheme } = useTheme()
  const badgeMode: BadgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'
  const [activeAccordionKey, setActiveAccordionKey] = useState<string | null>(
    null,
  )
  const [previousAppointmentsList, setPreviousAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const [upcomingAppointmentsList, setUpcomingAppointmentsList] = useState<
    AppointmentResponse[]
  >([])
  const [currentAppt, setCurrentAppt] = useState<AppointmentResponse | null>(
    null,
  )
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] =
    useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [apptIdToDelete, setApptIdToDelete] = useState<string | null>(null)
  const [searchText, setSearchText] = useState<string>('')
  const [selectedFilters, setSelectedFilters] = useState<FilterSelection>({})
  const [filterResetKey, setFilterResetKey] = useState<number>(0)
  const [customDateRange, setCustomDateRange] = useState<{
    start: Date
    end: Date
  }>({
    start: moment().subtract(3, 'months').startOf('month').toDate(),
    end: moment().toDate(),
  })
  const refresh = useAppSelector((state) => state.patient.refresh)
  const [loadedProviderId, setLoadedProviderId] = useState<string | null>(null)
  const isLoading =
    Boolean(provider) && loadedProviderId !== provider?.provider_id
  const hasNoAppointments =
    !isLoading &&
    previousAppointmentsList.length === 0 &&
    upcomingAppointmentsList.length === 0

  const [historyTooltipContainer, setHistoryTooltipContainer] =
    useState<HTMLDivElement | null>(null)

  useLayoutEffect(() => {
    const el = document.createElement('div')
    el.className = 'history-tooltip-portal-root'
    el.setAttribute('data-provider-history-tooltip-portal', '')
    document.body.appendChild(el)
    setHistoryTooltipContainer(el)
    return () => {
      el.remove()
    }
  }, [])

  const statusOptions: { label: string; value: AppointmentStatus }[] = [
    { label: 'Confirmed', value: AppointmentStatus.Confirmed },
    { label: 'Completed', value: AppointmentStatus.Completed },
    { label: 'Pending', value: AppointmentStatus.Pending },
    { label: 'Follow-up', value: AppointmentStatus.FollowUp },
    { label: 'New patient', value: AppointmentStatus.NewPatient },
    { label: 'Cancelled', value: AppointmentStatus.Cancelled },
    { label: 'No show', value: AppointmentStatus.NoShow },
    { label: 'Unconfirmed', value: AppointmentStatus.Unconfirmed },
    { label: 'Check-in', value: AppointmentStatus.CheckIn },
    { label: 'In treatment', value: AppointmentStatus.InTreatment },
    { label: 'Check-out', value: AppointmentStatus.CheckOut },
  ]

  const statusLabelByValue = statusOptions.reduce(
    (acc, curr) => {
      acc[curr.value] = curr.label
      return acc
    },
    {} as Record<AppointmentStatus, string>,
  )

  const monthOptions = [
    'Past 3 months',
    'Past 6 months',
    'Past 12 months',
    'Custom',
  ]
  const monthLookup: Record<string, number> = {
    'Past 3 months': 3,
    'Past 6 months': 6,
    'Past 12 months': 12,
  }

  const appointmentTypeOptions = useMemo(() => {
    const uniqueTypes = new Set<string>()
    ;[...previousAppointmentsList, ...upcomingAppointmentsList].forEach(
      (appt) => {
        if (appt.appointmenttype?.name) {
          uniqueTypes.add(appt.appointmenttype.name)
        }
      },
    )
    return Array.from(uniqueTypes).sort()
  }, [previousAppointmentsList, upcomingAppointmentsList])

  const filterAppointments = (
    appointmentsData: AppointmentResponse[],
    isUpcoming: boolean = false,
  ): AppointmentResponse[] => {
    const currentDate = new Date()
    const monthSelections = selectedFilters['Months'] || []
    const selectedStatuses = new Set(selectedFilters['Status'] || [])
    const selectedAppointmentTypes = new Set(
      selectedFilters['Appointment type'] || [],
    )
    const query = searchText.trim().toLowerCase()

    return appointmentsData.filter((appointment) => {
      const appointmentDate = getAppointmentLocalMoment(appointment)
      const patientName =
        `${appointment.patient?.first_name ?? ''} ${appointment.patient?.last_name ?? ''}`.toLowerCase()
      const apptType = appointment.appointmenttype?.name || ''
      const searchIncluded =
        patientName.includes(query) || apptType.toLowerCase().includes(query)
      const statusLabel =
        statusLabelByValue[appointment.appt_status as AppointmentStatus]

      let monthIncluded = true
      if (!isUpcoming && monthSelections.length > 0) {
        if (monthSelections.includes('Custom')) {
          const appointmentDateOnly = appointmentDate.clone().startOf('day')
          monthIncluded =
            appointmentDateOnly.isSameOrAfter(
              moment(customDateRange.start).startOf('day'),
            ) &&
            appointmentDateOnly.isSameOrBefore(
              moment(customDateRange.end).endOf('day'),
            )
        } else {
          const validSelections = monthSelections.filter(
            (label) => label !== 'Custom',
          )
          if (validSelections.length > 0) {
            const subtractMonths = Math.max(
              ...validSelections.map((label) => monthLookup[label] || 3),
            )
            const rangeStart = moment(currentDate)
              .subtract(subtractMonths, 'months')
              .startOf('month')
            const apptMonthStart = appointmentDate.clone().startOf('month')
            monthIncluded =
              apptMonthStart.isSameOrAfter(rangeStart) &&
              apptMonthStart.isBefore(currentDate)
          }
        }
      }

      const statusIncluded =
        selectedStatuses.size === 0 || selectedStatuses.has(statusLabel)
      const appointmentTypeIncluded =
        selectedAppointmentTypes.size === 0 ||
        selectedAppointmentTypes.has(apptType)

      return (
        monthIncluded &&
        statusIncluded &&
        appointmentTypeIncluded &&
        searchIncluded
      )
    })
  }

  const customDateRangeRenderer = (
    option: string,
    isChecked: boolean,
    onChange: () => void,
  ): React.ReactNode => {
    const applyRange = (e: any): void => {
      if (e.startDate && e.endDate) {
        setCustomDateRange({ start: e.startDate, end: e.endDate })
      }
    }
    return (
      <div
        className="custom-date-range-filter"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="d-flex align-items-center gap-2">
          <Form.Check
            type="checkbox"
            label={option}
            checked={isChecked}
            onChange={onChange}
            className="mb-0"
          />
        </div>
        {isChecked && (
          <div className="custom-date-range-picker-inline">
            <DateRangePickerComponent
              placeholder="Select date range"
              value={[customDateRange.start, customDateRange.end]}
              change={applyRange}
              select={applyRange}
              format="MM/dd/yyyy"
              showClearButton={false}
              openOnFocus={true}
              allowEdit={false}
            />
          </div>
        )}
      </div>
    )
  }

  const onEditAppointment = (apptId: string): void => {
    // Prevent editing mock appointments
    if (isMockAppointment(apptId)) {
      return
    }
    setCurrentAppt(
      upcomingAppointmentsList.find((appt) => appt.appointment_id === apptId) ||
        null,
    )
    setIsSchedulingModalOpen(true)
  }

  const onDeleteAppointment = (apptId: string): void => {
    // Prevent deleting mock appointments
    if (isMockAppointment(apptId)) {
      return
    }
    setApptIdToDelete(apptId)
    setIsDeleteModalOpen(true)
  }

  const appointmentCardElement = (
    appt: AppointmentResponse,
    isUpcoming: boolean,
  ): JSX.Element => {
    const statusType =
      AppointmentStatusMap[appt.appt_status as AppointmentStatus]

    return (
      <div key={appt.appointment_id} className="sp-history-appt-card">
        <div className="sp-history-appt-card-top">
          <div className="sp-history-appt-title-row">
            <span className="sp-history-appt-type">
              {appt.appointmenttype.name}
            </span>
            <span className="sp-history-appt-provider">
              {`${appt.patient?.first_name ?? ''} ${appt.patient?.last_name ?? ''}`.trim()}
            </span>
          </div>
          <span className="sp-history-appt-date">
            {formatAppointmentLocalDate(appt, 'MM-DD-YYYY')}
          </span>
        </div>
        <div className="sp-history-appt-card-bottom">
          <Badge
            label={getHistoryStatusLabel(statusType)}
            color={getHistoryStatusColor(statusType, isUpcoming)}
            mode={badgeMode}
          />
        </div>
      </div>
    )
  }

  const historyTooltip = (
    data: AppointmentResponse,
    index: number,
    isUpcoming: boolean,
  ): JSX.Element => {
    return (
      <OverlayTrigger
        key={index}
        placement="top"
        transition={false}
        container={historyTooltipContainer ?? undefined}
        popperConfig={{ strategy: 'fixed' }}
        overlay={
          <Tooltip id={`tooltip-${index}`} className="history-tt-custom">
            <div className="d-flex mb-1 tt-title w-100">Appointment Info</div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label ">Appt. Type:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                {data.appointmenttype.name}
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Date & Time:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <div>{formatAppointmentLocalDate(data, 'MMM DD, YYYY')}</div>
                <div>{formatAppointmentLocalStartTime(data)}</div>
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Procedure:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                {data.procedure_logs
                  .map((procedure) => procedure.proc_code)
                  .join(',')}
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Status:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <StatusBadge
                  status={
                    AppointmentStatusMap[data.appt_status as AppointmentStatus]
                  }
                />
              </div>
            </div>

            <div className="d-flex mb-1 w-100">
              <div className="w-50 text-end pe-2">
                <strong className="tt-label">Note:</strong>
              </div>
              <div className="w-50 text-start tt-value">
                <span className="">
                  {htmlToPlainTextPreview(data.notes?.[0]?.body)}
                </span>
              </div>
            </div>
          </Tooltip>
        }
      >
        {appointmentCardElement(data, isUpcoming)}
      </OverlayTrigger>
    )
  }

  useEffect(() => {
    const fetchAppointments = async (): Promise<void> => {
      if (provider) {
        try {
          const currentDate = new Date()
          const responsePrevious = await getAppointmentsByProviderId(
            provider.provider_id,
            moment(currentDate).format('YYYY-MM-DD'),
            undefined,
          )
          const previousAppointments =
            responsePrevious?.data?.appointments || []

          // Use real data if available, otherwise fallback to mock data in development only
          setPreviousAppointmentsList(
            previousAppointments.length > 0
              ? previousAppointments
              : process.env.NODE_ENV === 'development'
                ? MOCK_PREVIOUS_APPOINTMENTS(provider.provider_id)
                : [],
          )

          const responseUpcoming = await getAppointmentsByProviderId(
            provider.provider_id,
            undefined,
            moment(currentDate).format('YYYY-MM-DD'),
          )
          const upcomingAppointments =
            responseUpcoming?.data?.appointments || []
          setUpcomingAppointmentsList(upcomingAppointments)
        } catch (error) {
          // TEMP (dev only): show mock appointments when the backend is unreachable.
          if (process.env.NODE_ENV !== 'development') throw error
          setPreviousAppointmentsList(
            MOCK_PREVIOUS_APPOINTMENTS(provider.provider_id),
          )
          setUpcomingAppointmentsList([])
        } finally {
          // Always leave the skeleton state, even if a request fails
          setLoadedProviderId(provider.provider_id)
        }
      }
    }
    fetchAppointments()
  }, [refresh, provider])

  // Re-open the most recent month when the list or filters change, so a
  // user-collapsed item stays closed otherwise
  useEffect(() => {
    setActiveAccordionKey(
      filterAppointments(previousAppointmentsList).length > 0 ? '0' : null,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previousAppointmentsList, selectedFilters, searchText, customDateRange])

  const groupByApptDate = (
    appointmentsData: AppointmentResponse[],
  ): Record<string, AppointmentResponse[]> => {
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'long' })

    return appointmentsData.reduce(
      (grouped, appointment) => {
        const monthName = formatter.format(
          getAppointmentLocalMoment(appointment).toDate(),
        )

        if (!grouped[monthName]) {
          grouped[monthName] = []
        }

        grouped[monthName].push(appointment)
        return grouped
      },
      {} as Record<string, AppointmentResponse[]>,
    )
  }

  const previousAppointmentsElement = (): JSX.Element[] => {
    const groupedAppointments = groupByApptDate(
      filterAppointments(previousAppointmentsList),
    )
    if (Object.keys(groupedAppointments).length === 0) {
      return [
        <EmptyState
          key="no-appointments"
          className="sp-history-empty-state sp-history-empty-state--compact"
          iconClass="mgc mgc_calendar_fill"
          title="No previous appointments"
        />,
      ]
    }
    return Object.entries(groupedAppointments).map(
      ([month, appointmentsData], idx) => {
        return (
          <Accordion.Item eventKey={idx.toString()} key={idx}>
            <Accordion.Header>
              <span className="sp-history-month-arrow" aria-hidden>
                <ArrowRightMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--right" />
                <ArrowDownMonthIcon className="sp-history-month-arrow-icon sp-history-month-arrow-icon--down" />
              </span>
              <span className="sp-history-month-label">{month}</span>
            </Accordion.Header>
            <Accordion.Body>
              <div className="sp-history-appt-list sp-history-appt-list--nested">
                {appointmentsData
                  .slice()
                  .reverse()
                  .map((item, itemIdx) => historyTooltip(item, itemIdx, false))}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )
      },
    )
  }

  const handleSaveAppointment = (
    apptDetails: Partial<AppointmentRequest>,
  ): void => {
    if (
      !apptDetails?.appointment_id ||
      isMockAppointment(apptDetails.appointment_id)
    ) {
      return
    }
    updateAppointmentById(apptDetails.appointment_id, apptDetails).then(() =>
      dispatch(refreshFromPatient()),
    )
  }

  const handleDeleteAppointment = (apptId: string): void => {
    if (isMockAppointment(apptId)) {
      setIsDeleteModalOpen(false)
      return
    }
    deleteAppointmentById(apptId).then(() => {
      setIsDeleteModalOpen(false)
      dispatch(refreshFromPatient())
    })
  }

  const filteredUpcomingAppointments = filterAppointments(
    upcomingAppointmentsList,
    true,
  )
  // Soonest first, grouped by month (first month open by default).
  const upcomingMonthGroups = groupAppointmentsByMonth(
    filteredUpcomingAppointments
      .slice()
      .sort(
        (a, b) =>
          getAppointmentLocalMoment(a).valueOf() -
          getAppointmentLocalMoment(b).valueOf(),
      ),
  )

  return (
    <>
      {hasNoAppointments ? (
        <EmptyState
          className="sp-history-empty-state"
          iconClass="mgc mgc_calendar_fill"
          title="No appointments found"
          description="This staff member doesn't have any appointments on record yet."
        />
      ) : (
        <div className="sp-history" aria-busy={isLoading}>
          <section className="sp-history-section">
            <HistorySectionHeader title="Upcoming appointments" />
            <div className="sp-history-search">
              <SearchWithFilter
                key={filterResetKey}
                className="sp-history-search-swf"
                searchIcon="mgc mgc_search_2_line"
                filterIcon="mgc mgc_settings_2_line"
                placeholder="Search"
                searchQuery={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                filters={{
                  Status: statusOptions.map((option) => option.label),
                  Months: monthOptions,
                  'Appointment type': appointmentTypeOptions,
                }}
                filterLabels={{
                  Status: 'Status',
                  Months: 'Months',
                  'Appointment type': 'Appointment type',
                }}
                onFilterChange={(filters) => setSelectedFilters(filters)}
                customRenderers={{
                  'Months:Custom': customDateRangeRenderer,
                }}
                showResetButton
                onReset={() => {
                  setSelectedFilters({})
                  setSearchText('')
                  setCustomDateRange({
                    start: moment()
                      .subtract(3, 'months')
                      .startOf('month')
                      .toDate(),
                    end: moment().toDate(),
                  })
                  setFilterResetKey((prev) => prev + 1)
                }}
              />
            </div>
            {isLoading ? (
              <HistoryUpcomingSkeleton />
            ) : filteredUpcomingAppointments.length === 0 ? (
              <EmptyState
                className="sp-history-empty-state sp-history-empty-state--compact"
                iconClass="mgc mgc_calendar_fill"
                title="No upcoming appointments"
              />
            ) : (
              <HistoryMonthAccordion
                groups={upcomingMonthGroups}
                renderAppointment={(item, idx) =>
                  historyTooltip(item, idx, true)
                }
              />
            )}
          </section>
          <section className="sp-history-section">
            <HistorySectionHeader title="Previous appointments" />
            {isLoading ? (
              <HistoryPreviousSkeleton />
            ) : (
              <Accordion
                activeKey={activeAccordionKey}
                onSelect={(key) =>
                  setActiveAccordionKey(
                    Array.isArray(key) ? (key[0] ?? null) : (key ?? null),
                  )
                }
                className="sp-history-month-accordion"
              >
                {previousAppointmentsElement()}
              </Accordion>
            )}
          </section>
        </div>
      )}
      <ConfirmationModal
        show={isDeleteModalOpen}
        onHide={() => setIsDeleteModalOpen(false)}
        title="Cancel schedule"
        className="confirmation-modal--alert"
        headerClassName="confirmation-modal__header"
        size="md"
        body={
          apptIdToDelete !== null ? (
            deleteModalBody(apptIdToDelete, upcomingAppointmentsList)
          ) : (
            <div></div>
          )
        }
        footer={
          apptIdToDelete !== null ? (
            deleteModalFooter(
              apptIdToDelete,
              dispatch,
              () => setIsDeleteModalOpen(false),
              () => handleDeleteAppointment(apptIdToDelete),
            )
          ) : (
            <div></div>
          )
        }
      />
    </>
  )
}

export default History
